import type { IQuestService, CreateQuestOptions, UpdateQuestOptions } from "@/services/interfaces/IQuestService";
import { QuestNotFoundError } from "@/lib/questErrors";
import type {
  Quest,
  Task,
  TaskTreeNode,
  CreateQuestInput,
  CreateTaskInput,
  QuestFilters,
  PaginatedResult,
} from "@/lib/types";
import { isVolunteerQuest } from "@/lib/utils";
import { canEditTrustFactors } from "@/lib/trustGrade/editing";
import { sanitizeTrustFactors, gradeFromFactors } from "@/lib/trustGrade/rubric";

const MOCK_DELAY_MS = 800;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function generateId(): string {
  return `mock-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const MOCK_QUESTS: Quest[] = [
  {
    id: "mock-quest-001",
    creatorId: "user-dev-1",
    title: "Build a Landing Page",
    description:
      "Design and develop a responsive landing page for our new product launch. Must include hero section, features grid, testimonials, and CTA.",
    category: "Development",
    status: "active",
    totalBudgetUSD: 500,
    currency: "USDT",
    deadline: new Date(Date.now() + 14 * 86400000).toISOString(),
    attachments: [],
    locationRequirement: "remote",
    createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 86400000).toISOString(),
    taskCount: 3,
    completedTaskCount: 1,
    // DEV-824: one fixture carries real translations so the mock adapter can
    // exercise the language toggle on /work without a Supabase stack behind it.
    i18n: {
      es: {
        title: "Crear una página de aterrizaje",
        description:
          "Diseña y desarrolla una página de aterrizaje adaptable para el lanzamiento de nuestro nuevo producto. Debe incluir sección principal, cuadrícula de funcionalidades, testimonios y llamada a la acción.",
      },
      pt: {
        title: "Criar uma página de destino",
        description:
          "Projete e desenvolva uma página de destino responsiva para o lançamento do nosso novo produto. Deve incluir seção principal, grade de funcionalidades, depoimentos e chamada para ação.",
      },
    },
    sourceLang: "en",
  },
  {
    id: "mock-quest-002",
    creatorId: "user-dev-1",
    title: "Write Technical Blog Series",
    description:
      "Create a 5-part blog series explaining our smart contract architecture, including code walkthroughs and diagrams.",
    category: "Writing",
    status: "draft",
    totalBudgetUSD: 300,
    currency: "USDT",
    deadline: new Date(Date.now() + 30 * 86400000).toISOString(),
    attachments: [],
    locationRequirement: "remote",
    createdAt: new Date(Date.now() - 1 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 86400000).toISOString(),
    taskCount: 5,
    completedTaskCount: 0,
  },
  {
    id: "mock-quest-003",
    creatorId: "user-dev-1",
    title: "Design Brand Identity",
    description:
      "Create a complete brand identity kit including logo variations, color palette, typography guidelines, and social media templates.",
    category: "Design",
    status: "completed",
    totalBudgetUSD: 800,
    currency: "USDT",
    deadline: null,
    attachments: [],
    locationRequirement: "remote",
    createdAt: new Date(Date.now() - 30 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    taskCount: 4,
    completedTaskCount: 4,
  },
  // DEV-809 review pane: the outreach drafts in MockStaffingService point at
  // these two ids. Without them `useQuest` fails and the Outbox shows "Failed
  // to load quest details" for every draft — a fixture gap that reads exactly
  // like a product bug.
  // A volunteer quest (zero budget), so the volunteer surfaces — the rose
  // badge, the volunteer apply flow, the share poster's volunteer variant —
  // can be exercised without a Supabase stack. Written in Spanish on purpose:
  // the en/pt translations exercise the non-English source path.
  {
    id: "mock-quest-004",
    creatorId: "user-dev-1",
    // Posted as Fundación Luque Verde (MockOrganizationService).
    organizationId: "org-luque-verde",
    title: "Pintar el salón comunitario",
    description:
      "Buscamos **voluntarios** para pintar el salón del barrio antes de la feria del sábado. La pintura y los pinceles los ponemos nosotros; traé ropa cómoda.",
    category: "Community",
    status: "active",
    totalBudgetUSD: 0,
    currency: "USDT",
    deadline: new Date(Date.now() + 10 * 86400000).toISOString(),
    attachments: [],
    locationRequirement: "onsite",
    createdAt: new Date(Date.now() - 2 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 86400000).toISOString(),
    taskCount: 2,
    completedTaskCount: 0,
    i18n: {
      en: {
        title: "Paint the community hall",
        description:
          "We're looking for **volunteers** to paint the neighbourhood hall before Saturday's fair. We provide the paint and brushes; bring comfortable clothes.",
      },
      pt: {
        title: "Pintar o salão comunitário",
        description:
          "Procuramos **voluntários** para pintar o salão do bairro antes da feira de sábado. A tinta e os pincéis são por nossa conta; venha com roupa confortável.",
      },
    },
    sourceLang: "es",
  },
  {
    id: "admin-quest-001",
    creatorId: "user-dev-1",
    title: "Build Payment Gateway",
    description:
      "Implement Stripe integration for the platform: hosted checkout, webhook handling for payment and refund events, and a reconciliation report for the finance team.",
    category: "Development",
    status: "active",
    totalBudgetUSD: 1200,
    currency: "USDT",
    deadline: new Date(Date.now() + 21 * 86400000).toISOString(),
    attachments: [],
    locationRequirement: "remote",
    createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 86400000).toISOString(),
    taskCount: 2,
    completedTaskCount: 0,
  },
  {
    id: "admin-quest-002",
    creatorId: "user-dev-1",
    title: "Design Landing Page",
    description:
      "Diseñar una landing page responsive para el lanzamiento de un producto nuevo: hero, grilla de características, testimonios y CTA final.",
    category: "Design",
    status: "active",
    totalBudgetUSD: 640,
    currency: "USDT",
    deadline: new Date(Date.now() + 12 * 86400000).toISOString(),
    attachments: [],
    locationRequirement: "onsite",
    createdAt: new Date(Date.now() - 4 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
    taskCount: 2,
    completedTaskCount: 0,
  },
];


const MOCK_TASKS: Task[] = [
  // Skills here are what the Outbox's "Skills needed" chips and the
  // n/m-matched claim are computed from, so both quests carry a realistic
  // spread: some the candidate has, some they do not.
  {
    id: "admin-task-001",
    trustGrade: 1,
    trustFactors: { access: 2, money: 1, data: 2, people: 4, safety: 4 },
    trustFactorsSource: "ai",
    trustSpecialSuggested: false,
    trustGradeOverridden: false,
    questId: "admin-quest-001",
    parentTaskId: null,
    title: "Stripe hosted checkout + webhooks",
    description: "Wire hosted checkout and handle payment_intent and refund webhook events idempotently.",
    taskType: "human",
    status: "open",
    budgetUSD: 800,
    estimatedEffortHours: 20,
    deadline: null,
    skills: ["Stripe", "Node.js", "TypeScript"],
    assigneeId: null,
    order: 1,
    workMode: "remote",
    locationCountries: [],
    locationCities: [],
    createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 86400000).toISOString(),
  },
  {
    id: "admin-task-002",
    trustGrade: 2,
    trustFactors: { access: 3, money: 2, data: 3, people: 4, safety: 4 },
    trustFactorsSource: "ai",
    trustSpecialSuggested: false,
    trustGradeOverridden: false,
    questId: "admin-quest-001",
    parentTaskId: null,
    title: "Reconciliation report",
    description: "Nightly job reconciling Stripe payouts against platform ledger entries.",
    taskType: "human",
    status: "open",
    budgetUSD: 400,
    estimatedEffortHours: 10,
    deadline: null,
    skills: ["PostgreSQL", "Accounting"],
    assigneeId: null,
    order: 2,
    workMode: "remote",
    locationCountries: [],
    locationCities: [],
    createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 86400000).toISOString(),
  },
  {
    id: "admin-task-003",
    trustGrade: 3,
    trustFactors: { access: 4, money: 4, data: 4, people: 4, safety: 4 },
    trustFactorsSource: "ai",
    trustSpecialSuggested: false,
    trustGradeOverridden: false,
    questId: "admin-quest-002",
    parentTaskId: null,
    title: "Hero y grilla de características",
    description: "Maquetar el hero y la grilla de características en Figma, con variantes mobile.",
    taskType: "human",
    status: "open",
    budgetUSD: 400,
    estimatedEffortHours: 12,
    deadline: null,
    skills: ["Figma", "CSS", "Responsive Design"],
    assigneeId: null,
    order: 1,
    workMode: "onsite",
    locationCountries: [],
    locationCities: ["Buenos Aires"],
    createdAt: new Date(Date.now() - 4 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 4 * 86400000).toISOString(),
  },
  {
    id: "admin-task-004",
    trustGrade: 2,
    trustFactors: { access: 4, money: 4, data: 3, people: 3, safety: 4 },
    trustFactorsSource: "ai",
    trustSpecialSuggested: false,
    trustGradeOverridden: false,
    questId: "admin-quest-002",
    parentTaskId: null,
    title: "Implementar la landing en React",
    description: "Pasar los mockups aprobados a componentes React con animaciones de scroll.",
    taskType: "human",
    status: "open",
    budgetUSD: 240,
    estimatedEffortHours: 8,
    deadline: null,
    skills: ["React", "Tailwind"],
    assigneeId: null,
    order: 2,
    workMode: "onsite",
    locationCountries: [],
    locationCities: ["Buenos Aires"],
    createdAt: new Date(Date.now() - 4 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 4 * 86400000).toISOString(),
  },

  {
    id: "mock-task-001",
    trustGrade: 4,
    trustFactors: { access: 4, money: 4, data: 4, people: 4, safety: 4 },
    trustFactorsSource: "ai",
    trustSpecialSuggested: false,
    trustGradeOverridden: false,
    questId: "mock-quest-001",
    parentTaskId: null,
    title: "Design wireframes and mockups",
    description:
      "Create wireframes for all landing page sections, then produce high-fidelity mockups in Figma.",
    taskType: "human",
    status: "completed",
    budgetUSD: 150,
    estimatedEffortHours: 6,
    deadline: null,
    skills: ["UI Design", "Figma"],
    assigneeId: "mock-hunter-001",
    order: 1,
    workMode: "remote",
    locationCountries: [],
    locationCities: [],
    createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
  },
  {
    id: "mock-task-002",
    trustGrade: 2,
    trustFactors: { access: 3, money: 4, data: 3, people: 4, safety: 4 },
    trustFactorsSource: "ai",
    trustSpecialSuggested: false,
    trustGradeOverridden: false,
    questId: "mock-quest-001",
    parentTaskId: null,
    title: "Develop frontend components",
    description:
      "Build responsive React components for the landing page based on approved designs.",
    taskType: "human",
    status: "in_progress",
    budgetUSD: 250,
    estimatedEffortHours: 12,
    deadline: new Date(Date.now() + 7 * 86400000).toISOString(),
    skills: ["React", "TypeScript", "Tailwind CSS"],
    assigneeId: "mock-hunter-002",
    order: 2,
    workMode: "remote",
    locationCountries: [],
    locationCities: [],
    createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 86400000).toISOString(),
  },
  {
    id: "mock-task-003",
    trustGrade: 4,
    trustFactors: { access: 4, money: 4, data: 4, people: 4, safety: 4 },
    trustFactorsSource: "ai",
    trustSpecialSuggested: false,
    trustGradeOverridden: false,
    questId: "mock-quest-001",
    parentTaskId: "mock-task-002",
    title: "Set up project scaffold",
    description:
      "Initialize Vite + React + Tailwind project with proper folder structure and linting.",
    taskType: "ai",
    status: "completed",
    budgetUSD: 25,
    estimatedEffortHours: 1,
    deadline: null,
    skills: ["React", "TypeScript"],
    assigneeId: null,
    order: 1,
    workMode: "remote",
    locationCountries: [],
    locationCities: [],
    createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
  },
  {
    id: "mock-task-004",
    trustGrade: 2,
    trustFactors: { access: 2, money: 4, data: 3, people: 4, safety: 4 },
    trustFactorsSource: "ai",
    trustSpecialSuggested: false,
    trustGradeOverridden: false,
    questId: "mock-quest-001",
    parentTaskId: null,
    title: "QA and cross-browser testing",
    description:
      "Test the landing page across browsers and devices, file bugs, and verify fixes.",
    taskType: "human",
    status: "open",
    budgetUSD: 100,
    estimatedEffortHours: 4,
    deadline: new Date(Date.now() + 12 * 86400000).toISOString(),
    skills: ["Testing", "QA"],
    assigneeId: null,
    order: 3,
    workMode: "remote",
    locationCountries: [],
    locationCities: [],
    createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 86400000).toISOString(),
  },
  {
    id: "mock-task-401",
    trustGrade: 0,
    trustFactors: { access: 1, money: 4, data: 2, people: 1, safety: 2 },
    trustFactorsSource: "ai",
    trustSpecialSuggested: true,
    trustGradeOverridden: true,
    questId: "mock-quest-004",
    parentTaskId: null,
    title: "Pintar las paredes",
    description: "Dos manos de pintura en la sala principal.",
    taskType: "human",
    status: "open",
    budgetUSD: 0,
    estimatedEffortHours: 4,
    deadline: null,
    skills: ["Pintura"],
    assigneeId: null,
    order: 1,
    workMode: "onsite",
    locationCountries: ["PY"],
    locationCities: ["encarnacion"],
    createdAt: new Date(Date.now() - 2 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
  },
  {
    id: "mock-task-402",
    trustGrade: 4,
    trustFactors: { access: 4, money: 4, data: 4, people: 4, safety: 4 },
    trustFactorsSource: "ai",
    trustSpecialSuggested: false,
    trustGradeOverridden: false,
    questId: "mock-quest-004",
    parentTaskId: null,
    title: "Limpiar y ordenar",
    description: "Cubrir el piso y dejar todo limpio al terminar.",
    taskType: "human",
    status: "open",
    budgetUSD: 0,
    estimatedEffortHours: 4,
    deadline: null,
    skills: ["Limpieza"],
    assigneeId: null,
    order: 2,
    workMode: "onsite",
    locationCountries: ["PY"],
    locationCities: ["encarnacion"],
    createdAt: new Date(Date.now() - 2 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
  },
];

/**
 * What set_quest_affinity snapshots from a quest (20261020100000), for the mock
 * user service — which has no other way to see these fixtures. Null when the
 * id is not a mock quest.
 */
export function mockQuestAffinityFeatures(
  questId: string,
): { category: string; skills: string[]; isVolunteer: boolean } | null {
  const quest = MOCK_QUESTS.find((q) => q.id === questId);
  if (!quest) return null;
  const skills = MOCK_TASKS.filter((t) => t.questId === questId).flatMap((t) => t.skills ?? []);
  return {
    category: quest.category ?? "",
    skills: [...new Set(skills)].sort(),
    isVolunteer: (quest.totalBudgetUSD ?? 0) === 0,
  };
}

export class MockQuestService implements IQuestService {
  private quests: Map<string, Quest> = new Map();
  private tasks: Map<string, Task> = new Map();

  constructor() {
    for (const quest of MOCK_QUESTS) {
      this.quests.set(quest.id, { ...quest });
    }
    for (const task of MOCK_TASKS) {
      this.tasks.set(task.id, { ...task });
    }
  }

  async createQuest(data: CreateQuestInput, options?: CreateQuestOptions): Promise<Quest> {
    const onStep = options?.onStepChange;

    // Simulate on-chain steps with delays
    onStep?.("approve", "active");
    await delay(MOCK_DELAY_MS);
    onStep?.("approve", "done");

    onStep?.("create", "active");
    await delay(MOCK_DELAY_MS);
    onStep?.("create", "done");

    onStep?.("fund", "active");
    await delay(MOCK_DELAY_MS);
    onStep?.("fund", "done");

    onStep?.("tasks", "active");
    await delay(MOCK_DELAY_MS);
    onStep?.("tasks", "done");

    onStep?.("publish", "active");

    const id = generateId();
    const now = new Date().toISOString();

    const quest: Quest = {
      id,
      creatorId: "user-dev-1",
      organizationId: data.organizationId ?? null,
      title: data.title,
      description: data.description,
      category: data.category,
      status: "draft",
      totalBudgetUSD: data.totalBudgetUSD,
      currency: data.currency,
      locationRequirement: "remote",
      deadline: data.deadline ?? null,
      attachments: [],
      createdAt: now,
      updatedAt: now,
      taskCount: data.tasks.length,
      completedTaskCount: 0,
    };

    this.quests.set(id, quest);

    // Create tasks for the quest
    for (const taskInput of data.tasks) {
      await this.addTask(id, taskInput);
    }

    onStep?.("publish", "done");
    return quest;
  }

  async publishForNegotiation(data: CreateQuestInput, options?: CreateQuestOptions): Promise<Quest> {
    const onStep = options?.onStepChange;

    // No on-chain steps — just save to DB
    onStep?.("db_save", "active");
    await delay(MOCK_DELAY_MS);

    const id = generateId();
    const now = new Date().toISOString();

    const quest: Quest = {
      id,
      creatorId: "user-dev-1",
      organizationId: data.organizationId ?? null,
      title: data.title,
      description: data.description,
      category: data.category,
      status: "published",
      totalBudgetUSD: data.totalBudgetUSD,
      currency: data.currency,
      locationRequirement: "remote",
      deadline: data.deadline ?? null,
      attachments: [],
      createdAt: now,
      updatedAt: now,
      taskCount: data.tasks.length,
      completedTaskCount: 0,
    };

    this.quests.set(id, quest);

    for (const taskInput of data.tasks) {
      await this.addTask(id, taskInput);
    }

    onStep?.("db_save", "done");
    return quest;
  }

  async lockFundsAndStart(questId: string, options?: CreateQuestOptions): Promise<Quest> {
    const onStep = options?.onStepChange;
    const quest = this.quests.get(questId);
    if (!quest) throw new Error(`Quest not found: ${questId}`);

    // Volunteer quests skip on-chain steps — only DB save
    const steps = isVolunteerQuest(quest.totalBudgetUSD)
      ? (["db_save"] as const)
      : (["approve", "create", "fund", "tasks", "publish", "assign", "db_save"] as const);

    for (const step of steps) {
      onStep?.(step, "active");
      await delay(MOCK_DELAY_MS);
      onStep?.(step, "done");
    }

    this.quests.set(questId, { ...quest, status: "active" });
    return this.quests.get(questId)!;
  }

  async getQuest(id: string): Promise<Quest> {
    await delay(500);

    const quest = this.quests.get(id);
    if (!quest) {
      // Typed, like the Supabase adapter: mock mode must reproduce the same
      // absent-vs-broken distinction, or the not-found UI cannot be developed
      // against it.
      throw new QuestNotFoundError(id);
    }
    return { ...quest };
  }

  async listMyQuests(
    _userId: string,
    filters?: QuestFilters,
  ): Promise<PaginatedResult<Quest>> {
    await delay(MOCK_DELAY_MS);

    let quests = Array.from(this.quests.values());

    if (filters?.status) {
      quests = quests.filter((q) => q.status === filters.status);
    }

    const limit = filters?.limit ?? 20;
    const data = quests.slice(0, limit);

    const lastItem = data[data.length - 1];

    return {
      data,
      total: quests.length,
      cursor: quests.length > limit && lastItem?.id ? lastItem.id : null,
      hasMore: quests.length > limit,
    };
  }

  /**
   * `_opts.asAdmin` is accepted and ignored: the mock has no row-level security
   * to model, so both write paths are the same in-memory patch (DEV-792). It is
   * in the signature so demo/offline mode does not diverge from the interface.
   */
  async updateQuest(
    id: string,
    data: Partial<Quest>,
    _opts?: UpdateQuestOptions,
  ): Promise<Quest> {
    await delay(MOCK_DELAY_MS);

    const quest = this.quests.get(id);
    if (!quest) {
      throw new Error(`Quest not found: ${id}`);
    }

    const updated: Quest = {
      ...quest,
      ...data,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.quests.set(id, updated);
    return { ...updated };
  }

  async deleteQuest(id: string): Promise<void> {
    await delay(500);

    if (!this.quests.has(id)) {
      throw new Error(`Quest not found: ${id}`);
    }

    // Remove associated tasks
    for (const [taskId, task] of this.tasks) {
      if (task.questId === id) {
        this.tasks.delete(taskId);
      }
    }

    this.quests.delete(id);
  }

  async addTask(questId: string, taskInput: CreateTaskInput): Promise<Task> {
    await delay(500);

    const quest = this.quests.get(questId);
    if (!quest) {
      throw new Error(`Quest not found: ${questId}`);
    }

    const id = generateId();
    const now = new Date().toISOString();

    const task: Task = {
      id,
      questId,
      parentTaskId: null,
      title: taskInput.title,
      description: taskInput.description,
      taskType: taskInput.taskType,
      status: "draft",
      budgetUSD: taskInput.budgetUSD ?? 0,
      estimatedEffortHours: taskInput.estimatedEffortHours,
      deadline: taskInput.deadline ?? null,
      skills: taskInput.skills,
      workMode: taskInput.workMode ?? "remote",
      locationCountries: taskInput.locationCountries ?? [],
      locationCities: taskInput.locationCities ?? [],
      assigneeId: null,
      order: taskInput.order ?? this.getNextOrder(questId, null),
      createdAt: now,
      updatedAt: now,
    };

    this.tasks.set(id, task);

    // Create subtasks if provided
    if (taskInput.subtasks) {
      for (const subtaskInput of taskInput.subtasks) {
        await this.addSubtask(id, subtaskInput);
      }
    }

    // Update quest task count
    quest.taskCount = this.getTasksForQuest(questId).length;
    this.quests.set(questId, quest);

    return { ...task };
  }

  async updateTask(taskId: string, data: Partial<Task>): Promise<Task> {
    await delay(500);

    const task = this.tasks.get(taskId);
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }

    // Same refusal as tasks_trust_1_guard: the grade is fixed at hire.
    if (data.trustFactors !== undefined && !canEditTrustFactors({ status: task.status })) {
      throw new Error(`tasks: trust factors are fixed once a task is ${task.status} (hired under this grade)`);
    }

    const updated: Task = {
      ...task,
      ...data,
      id: taskId,
      updatedAt: new Date().toISOString(),
    };
    if (data.trustFactors !== undefined) {
      updated.trustFactors = sanitizeTrustFactors(data.trustFactors);
      updated.trustFactorsSource = "creator_edited";
    }
    // Like tasks_trust_3_compute: factors OR budget changes regrade the task.
    if ((data.trustFactors !== undefined || data.budgetUSD !== undefined) && updated.trustGrade !== undefined) {
      const computed = gradeFromFactors(updated.trustFactors ?? {}, updated.budgetUSD);
      updated.trustSpecialSuggested = computed.specialSuggested;
      if (!updated.trustGradeOverridden) updated.trustGrade = computed.grade;
    }
    this.tasks.set(taskId, updated);
    return { ...updated };
  }

  async dropTask(taskId: string): Promise<void> {
    await delay(500);

    const task = this.tasks.get(taskId);
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }

    // Mirrors creator_drop_task (20260917100000): idempotent, and the row
    // SURVIVES as cancelled rather than being deleted. A mock that deleted it
    // would hide the case the completion predicate exists for — a terminal
    // task that still has to be counted.
    if (task.status === "cancelled") return;

    // The RPC's two refusals, mirrored so demo mode cannot do what production
    // forbids. Without these a demo drops a staffed or already-funded task and
    // the flow looks like it works.
    const quest = this.quests.get(task.questId);
    if (quest?.chainQuestId != null) {
      throw new Error("quest is already funded on-chain");
    }
    if (task.assigneeId !== null) {
      // The mock has no proposals table; assigneeId is how it records that
      // somebody was accepted onto this task. Same intent as the RPC's
      // accepted-proposal check: do not retire work out from under a person.
      throw new Error("task has an accepted proposal");
    }

    this.tasks.set(taskId, { ...task, status: "cancelled" });
  }

  async adminUpdateQuestLocation(
    questId: string,
    workMode: string,
    locationCountries: string[],
    locationCities: string[],
  ): Promise<void> {
    await delay(300);
    for (const task of this.tasks.values()) {
      if (task.questId === questId) {
        task.workMode = workMode as Task["workMode"];
        task.locationCountries = locationCountries;
        task.locationCities = locationCities;
      }
    }
  }

  async deleteTask(taskId: string): Promise<void> {
    await delay(500);

    const task = this.tasks.get(taskId);
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }

    // Remove child tasks recursively
    for (const [childId, childTask] of this.tasks) {
      if (childTask.parentTaskId === taskId) {
        this.tasks.delete(childId);
      }
    }

    this.tasks.delete(taskId);

    // Update quest task count
    const quest = this.quests.get(task.questId);
    if (quest) {
      quest.taskCount = this.getTasksForQuest(task.questId).length;
      this.quests.set(task.questId, quest);
    }
  }

  async addSubtask(
    parentTaskId: string,
    subtask: CreateTaskInput,
  ): Promise<Task> {
    await delay(500);

    const parentTask = this.tasks.get(parentTaskId);
    if (!parentTask) {
      throw new Error(`Parent task not found: ${parentTaskId}`);
    }

    const id = generateId();
    const now = new Date().toISOString();

    const task: Task = {
      id,
      questId: parentTask.questId,
      parentTaskId,
      title: subtask.title,
      description: subtask.description,
      taskType: subtask.taskType,
      status: "draft",
      budgetUSD: subtask.budgetUSD ?? 0,
      estimatedEffortHours: subtask.estimatedEffortHours,
      deadline: subtask.deadline ?? null,
      skills: subtask.skills,
      workMode: subtask.workMode ?? "remote",
      locationCountries: subtask.locationCountries ?? [],
      locationCities: subtask.locationCities ?? [],
      assigneeId: null,
      order: subtask.order ?? this.getNextOrder(parentTask.questId, parentTaskId),
      createdAt: now,
      updatedAt: now,
    };

    this.tasks.set(id, task);

    // Create nested subtasks if provided
    if (subtask.subtasks) {
      for (const nestedSubtask of subtask.subtasks) {
        await this.addSubtask(id, nestedSubtask);
      }
    }

    // Update quest task count
    const quest = this.quests.get(parentTask.questId);
    if (quest) {
      quest.taskCount = this.getTasksForQuest(parentTask.questId).length;
      this.quests.set(parentTask.questId, quest);
    }

    return { ...task };
  }

  async getTaskTree(questId: string): Promise<TaskTreeNode[]> {
    await delay(500);

    const questTasks = this.getTasksForQuest(questId);
    const rootTasks = questTasks
      .filter((t) => t.parentTaskId === null)
      .sort((a, b) => a.order - b.order);

    return rootTasks.map((task) => this.buildTreeNode(task, questTasks));
  }

  async getTaskQuestId(taskId: string): Promise<string | null> {
    await delay(100);
    return this.tasks.get(taskId)?.questId ?? null;
  }

  async publishQuest(id: string): Promise<Quest> {
    await delay(MOCK_DELAY_MS);

    const quest = this.quests.get(id);
    if (!quest) {
      throw new Error(`Quest not found: ${id}`);
    }

    const updated: Quest = {
      ...quest,
      status: "active",
      updatedAt: new Date().toISOString(),
    };
    this.quests.set(id, updated);

    // Mark draft tasks as open
    for (const [taskId, task] of this.tasks) {
      if (task.questId === id && task.status === "draft") {
        this.tasks.set(taskId, { ...task, status: "open" });
      }
    }

    return { ...updated };
  }

  async closeQuest(id: string): Promise<Quest> {
    await delay(MOCK_DELAY_MS);

    const quest = this.quests.get(id);
    if (!quest) {
      throw new Error(`Quest not found: ${id}`);
    }

    const updated: Quest = {
      ...quest,
      status: "closed",
      updatedAt: new Date().toISOString(),
    };
    this.quests.set(id, updated);
    return { ...updated };
  }

  async cancelQuest(id: string): Promise<Quest> {
    await delay(MOCK_DELAY_MS);

    const quest = this.quests.get(id);
    if (!quest) {
      throw new Error(`Quest not found: ${id}`);
    }

    const updated: Quest = {
      ...quest,
      status: "cancelled",
      updatedAt: new Date().toISOString(),
    };
    this.quests.set(id, updated);
    return { ...updated };
  }


  // ─── MockQuestStore ───────────────────────────────────
  // The narrow surface MockQuestTemplateService writes through: a stock
  // quest's anchor and its clones are ordinary quests and must live in the
  // same maps every other method here reads from.

  putQuest(quest: Quest): void {
    this.quests.set(quest.id, quest);
  }

  findQuest(id: string): Quest | undefined {
    return this.quests.get(id);
  }

  allQuests(): Quest[] {
    return Array.from(this.quests.values());
  }

  putTask(task: Task): void {
    this.tasks.set(task.id, task);
  }

  tasksForQuest(questId: string): Task[] {
    return this.getTasksForQuest(questId);
  }

  // ─── Private helpers ───────────────────────────────────

  private getTasksForQuest(questId: string): Task[] {
    return Array.from(this.tasks.values()).filter(
      (t) => t.questId === questId,
    );
  }

  private getNextOrder(questId: string, parentTaskId: string | null): number {
    const siblings = Array.from(this.tasks.values()).filter(
      (t) => t.questId === questId && t.parentTaskId === parentTaskId,
    );
    return siblings.length + 1;
  }

  private buildTreeNode(task: Task, allTasks: Task[]): TaskTreeNode {
    const children = allTasks
      .filter((t) => t.parentTaskId === task.id)
      .sort((a, b) => a.order - b.order)
      .map((child) => this.buildTreeNode(child, allTasks));

    return { ...task, children };
  }
}
